/* eslint-disable max-len */
import {useState, useRef} from 'react';
import classNames from 'classnames';
import assert from 'assert';
import {SidebarArrowLeftSvg, SidebarArrowRightSvg} from '@/icons';
import Icon, {IconSize} from '../Icon';
import {clearDragMask, createDragMask} from '@/utils/frameMaskUtils';
import {throttle} from 'lodash-es';
import Tooltip, {TooltipTrigger} from '../Tooltip';
import {useIntl} from 'react-intl';
import {DragOption} from '@/interface';
import styles from './index.module.scss';

const GripperClassName = 'sidebar-gripper';

export interface DragLineOptions {
    maxLeft: number;
    minLeft: number;
}
interface DragLineProps {
    sidebarDragOption: DragOption;
    onCollapse: () => void;
    onExpand: () => void;
    onDragWidthStart: () => null | DragLineOptions;
    onDragWidthEnd: () => void;
    onDragWithMove: (left: number) => void;
}
export default function DragLine(props: DragLineProps) {
    const {sidebarDragOption, onCollapse, onExpand, onDragWidthStart, onDragWidthEnd, onDragWithMove} = props;
    const {sidebarOpen} = sidebarDragOption;
    const [active, setActive] = useState(false);
    const [toolTipVisible, setToolTipVisible] = useState(false);
    const maxLeft = useRef(window.document.body.clientWidth);
    const minLeft = useRef(0);
    const moveX = useRef(0);
    const mouseStartLeft = useRef(0);
    const gripperRef = useRef<HTMLDivElement>(null);
    const intl = useIntl();
    const handleMouseMoveDragLine = throttle((event: MouseEvent) => {
        let curLeft = event.pageX;
        if (curLeft > maxLeft.current) {
            curLeft = maxLeft.current;
        }
        if (curLeft < minLeft.current) {
            curLeft = minLeft.current;
        }
        const dx = curLeft - mouseStartLeft.current;
        moveX.current = dx;
        onDragWithMove(moveX.current);
        mouseStartLeft.current = curLeft;
    }, 70);
    async function handleMouseUpDragLine() {
        onDragWidthEnd();
        clearDragMask();
        setActive(false);
        moveX.current = 0;
        window.document.removeEventListener('mousemove', handleMouseMoveDragLine);
        window.document.removeEventListener('mouseup', handleMouseUpDragLine);
    }
    function handleMouseDownDragLine(event: React.MouseEvent<HTMLDivElement>) {
        const target = event.target;
        assert(target instanceof Element);
        const gripper = target.closest(`.${GripperClassName}`);
        if (gripper || !sidebarOpen) {
            return;
        }
        const options = onDragWidthStart();
        if (options) {
            mouseStartLeft.current = event.pageX;
            moveX.current = 0;
            maxLeft.current = options.maxLeft;
            minLeft.current = options.minLeft;
            createDragMask();
            setActive(true);
            window.document.addEventListener('mousemove', handleMouseMoveDragLine);
            window.document.addEventListener('mouseup', handleMouseUpDragLine);
        }
    }
    function handleClick() {
        setToolTipVisible(false);
        if (sidebarOpen) {
            onCollapse();
            return;
        }
        onExpand();
    }

    function handleBeforeChange(visible: boolean) {
        setToolTipVisible(visible);
    }

    function handleCloseSidebarClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        const target = event?.target;
        assert(target instanceof Element);
        if (target.closest('.brick-popper-wrap')) {
            event?.preventDefault();
            return;
        }
        if (!sidebarDragOption.sidebarOpen) {
            onExpand();
        }
    }

    return (
        <>
            <div
                className={classNames(styles.dragLineContainer,
                    active && styles.active,
                    !sidebarOpen && styles.showButton,
                    sidebarOpen && styles.dragLineContainerHover
                )}
                onMouseDown={handleMouseDownDragLine}
                onClick={handleCloseSidebarClick}
            >
            </div>
            <Tooltip
                className={styles.gripperTip}
                content={intl.formatMessage({id: sidebarOpen ? 'hideSidebar' : 'showSidebar'})}
                trigger={TooltipTrigger.Hover}
                placement={'right'}
                visible={toolTipVisible}
                beforeChange={handleBeforeChange}
            >
                <div ref={gripperRef} className={classNames(styles.gripperWrapper)} onClick={handleClick}>
                    <div className={classNames(GripperClassName, styles.gripper)}>
                        <Icon svg={sidebarOpen ? SidebarArrowLeftSvg : SidebarArrowRightSvg} size={IconSize.SM} noSpace className={styles.gripperIcon} />
                    </div>
                </div>
            </Tooltip>
        </>
    );
}
